import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { FiArrowLeft, FiChevronRight, FiUser } from "react-icons/fi";
import { getEventPointsLedger } from "../api/eventPointsApi";
import { getEvents, getEventParticipants } from "../api/eventApi";
import { calculateUserCoins } from "../utils/eventCoinUtils";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

const calculateDuration = (start, end) => {
  if (!start || !end) return "—";
  try {
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    const diff = e - s;
    if (isNaN(diff) || diff < 0) return "—";
    
    const totalSeconds = Math.floor(diff / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s_rem = totalSeconds % 60;

    let res = [];
    if (h > 0) res.push(`${h}h`);
    if (m > 0 || h > 0) res.push(`${m}m`);
    res.push(`${s_rem}s`);
    return res.join(" ");
  } catch {
    return "—";
  }
};

const formatPoints = (amt) => {
  if (amt == null || isNaN(Number(amt))) return "0";
  const num = Number(amt);
  return Number.isInteger(num)
    ? num.toLocaleString()
    : Number(num.toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatSource = (item) => {
  const src = String(item.source || "").toLowerCase();
  const details = String(item.details || item.reason || "");
  const isDebit = String(item.transactionType || item.type || "").toUpperCase() === "DEBIT";

  if (isDebit || details.toLowerCase().includes("revert")) return "Point Reversion";
  if (src.includes("check") || details.toLowerCase().includes("check-in") || details.toLowerCase().includes("checkin")) return "Daily Check-in";
  if (src.includes("refer") || details.toLowerCase().includes("referral")) return "Referral";
  if (src.includes("mining") || details.toLowerCase().includes("mining")) return "Mining Cycle";
  if (src.includes("browser") || details.toLowerCase().includes("browser")) return "Zeta Browser";

  if (details.includes("777 Slots") || src.includes("slot")) return "Play Slot Game";
  if (details.includes("Tap Game") || src.includes("tap")) return "Play Tap Game";
  if (details.includes("Boom Cards") || src.includes("boom")) return "Play Boom Cards";
  if (details.includes("Spin Wheel") || src.includes("spin")) return "Play Spin Wheel";
  if (details.includes("2048") || src.includes("2048")) return "Play 2048 Game";
  if (details.includes("Fruit Bomb") || src.includes("fruit")) return "Play Fruit Bomb";
  if (details.toLowerCase().includes("timer") || src.includes("timer")) return "Timer Reward";
  if (src.includes("game")) return "Play Mini Game";

  return item.sourceLabel || item.source || "Event Activity";
};

const parseHistoryList = (res) => {
  if (Array.isArray(res?.data?.history)) return res.data.history;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.history)) return res.history;
  if (Array.isArray(res)) return res;
  return [];
};

const extractAmount = (item) => {
  const candidates = [
    item.amount,
    item.points,
    item.eventPoints,
    item.pointsAwarded,
    item.claimedPoints,
    item.coins,
    item.coin,
    item.point,
  ];
  for (const value of candidates) {
    if (value != null && value !== "" && !isNaN(Number(value))) {
      return Number(value);
    }
  }
  return 0;
};

const SOURCE_OPTIONS = [
  { value: "all", label: "All Sources" },
  { value: "event_points", label: "Event Points" },
  { value: "daily_checkin", label: "Daily Check-in" },
  { value: "referral", label: "Referral" },
  { value: "mini_games", label: "Play Mini Game" },
  { value: "tap", label: "Play Tap Game" },
  { value: "2048", label: "Play 2048 Game" },
  { value: "fruit_bomb", label: "Play Fruit Bomb" },
  { value: "spinwheel", label: "Play Spin Wheel" },
  { value: "boomcards", label: "Play Boom Cards" },
  { value: "slots", label: "Play Slot Game" },
  { value: "timer", label: "Timer Reward" },
  { value: "mining", label: "Mining Cycle" },
  { value: "browser", label: "Zeta Browser" },
  { value: "ban", label: "Point Reversion" },
];

export default function EventActivityAudit() {
  const { id: paramId, userId: paramUserId, eventId: paramEventId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const userId = paramId || paramUserId || "";
  const initialEventId =
    paramEventId || searchParams.get("eventId") || location.state?.eventId || "all";
  const [selectedEventId, setSelectedEventId] = useState(initialEventId);
  const [eventsList, setEventsList] = useState([]);
  const [selectedSource, setSelectedSource] = useState("all");
  const [eventOriginalScore, setEventOriginalScore] = useState(
    Number(location.state?.originalCoins) > 0 ? Number(location.state.originalCoins) : null
  );

  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const pageBalancesRef = React.useRef({});

  useEffect(() => {
    pageBalancesRef.current = {};
  }, [selectedEventId, selectedSource, userId]);

  const handleSort = (key) => {
    let direction = 'descending';
    if (sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    } else if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = null;
      key = null;
    }
    setSortConfig({ key, direction });
  };

  const sortedHistory = React.useMemo(() => {
    let sortableItems = [...history];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [history, sortConfig]);

  // Fetch events list for dropdown
  useEffect(() => {
    const fetchEventsDropdown = async () => {
      try {
        const res = await getEvents({ page: 1, limit: 100 });
        const list = res?.data?.events || (Array.isArray(res?.data) ? res.data : []);
        setEventsList(list);
      } catch (err) {
        console.warn("Could not load events list:", err);
      }
    };
    fetchEventsDropdown();
  }, []);

  // Official participant score for the selected event (source of truth for running score)
  useEffect(() => {
    if (!userId) {
      setEventOriginalScore(null);
      return;
    }

    let cancelled = false;

    if (!selectedEventId || selectedEventId === "all") {
      setEventOriginalScore(null);
      const loadUserBalance = async () => {
        try {
          const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
          const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://api.minezeta.com/admin";
          const response = await fetch(`${baseUrl}/users/view/${userId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });
          const resJson = await response.json();
          if (!cancelled && resJson?.data?.coin != null) {
            setEventOriginalScore(Number(resJson.data.coin));
          }
        } catch (e) {
          console.warn("Could not load user balance", e);
        }
      };
      loadUserBalance();
      return () => {
        cancelled = true;
      };
    }

    const seededScore =
      location.state?.eventId &&
      String(location.state.eventId) === String(selectedEventId) &&
      Number(location.state.originalCoins) > 0
        ? Number(location.state.originalCoins)
        : null;
    if (seededScore != null) {
      setEventOriginalScore(seededScore);
    }
    const loadOfficialScore = async () => {
      try {
        const res = await getEventParticipants(selectedEventId, {
          page: 1,
          limit: 20,
          search: String(userId),
        });
        const responseData = res?.data || res;
        const list =
          responseData?.participants ||
          responseData?.history ||
          (Array.isArray(responseData) ? responseData : []);
        const match = (list || []).find(
          (u) => String(u.userId || u.id) === String(userId)
        );
        if (!match) return;
        const selEv = eventsList.find(
          (e) => String(e.eventId || e.id || e._id) === String(selectedEventId)
        );
        const { originalCoins } = calculateUserCoins(match, {}, selEv, selectedEventId);
        if (!cancelled && originalCoins > 0) {
          setEventOriginalScore(originalCoins);
        }
      } catch (err) {
        console.warn("Could not load official event score:", err);
      }
    };
    loadOfficialScore();
    return () => {
      cancelled = true;
    };
  }, [userId, selectedEventId, eventsList, location.state]);

  // Server-side paginated fetch: 1 request for the current page only
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const query = {
        page: currentPage,
        perPage: limit,
        source: selectedSource !== "all" ? selectedSource : "all",
        eventId: selectedEventId !== "all" ? selectedEventId : "",
      };

      const res = await getEventPointsLedger({ userId, ...query });

      const list = parseHistoryList(res);
      const pagination = res?.data?.pagination || res?.pagination || {};
      const total = Number(
        pagination.total ?? (res?.data?.total || res?.total || list.length)
      );
      const pages = Number(
        pagination.totalPages ??
          pagination.lastPage ??
          (total > 0 ? Math.ceil(total / limit) : 1)
      );

      setTotalRecords(total);
      setTotalPages(Math.max(1, pages));

      const eventActivities = list.map((item, idx) => {
        const rawAmt = extractAmount(item);
        const type = String(item.transactionType || item.type || "").toUpperCase();
        const isDebit = type === "DEBIT" || rawAmt < 0;
        const amt = Math.abs(rawAmt) || 0;
        const signedAmt = isDebit ? -amt : amt;

        return {
          id: item.id || item._id || `act-${idx}`,
          sourceName: formatSource(item),
          signedAmt,
          amount: amt,
          isDebit,
          balanceAfter:
            item.balanceAfter != null && !isNaN(Number(item.balanceAfter))
              ? Number(item.balanceAfter)
              : null,
          eventId: item.eventId || item.event_id || "",
          eventName: item.eventName || item.event_name || "",
          details: item.details || item.reason || item.note || item.eventTitle || "",
          createdAt: item.transactionDate || item.createdAt || new Date().toISOString(),
          startTime: item.startTime || item.start_time || item.sessionStartTime || item.sessionStart || item.startedAt || item.startDate || item.start_date || null,
          endTime: item.endTime || item.end_time || item.sessionEndTime || item.sessionEnd || item.endedAt || item.endDate || item.end_date || null,
        };
      });

      // Calculate running points across pages using pageBalancesRef
      let startingBalance = null;
      if (currentPage === 1 && eventOriginalScore != null) {
        startingBalance = Number(eventOriginalScore);
      } else if (pageBalancesRef.current[currentPage] != null) {
        startingBalance = pageBalancesRef.current[currentPage];
      }

      if (startingBalance != null) {
        let running = startingBalance;
        eventActivities.forEach((item) => {
          item.pointsAfter = running;
          running -= item.signedAmt;
        });
        pageBalancesRef.current[currentPage + 1] = running;
      }

      setHistory(eventActivities);
    } catch (err) {
      console.error("Failed to load user event details:", err);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [userId, currentPage, limit, selectedSource, selectedEventId, eventOriginalScore]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-6 sm:p-8 space-y-6 w-full">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium flex-wrap">
            <Link to="/events" className="hover:text-black hover:underline transition">
              Events
            </Link>
            <FiChevronRight size={12} className="text-gray-400" />
            <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
              User Event Details
            </span>
          </div>

          {/* Simple Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 w-full">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition shadow-2xs cursor-pointer shrink-0"
                title="Back"
              >
                <FiArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-2xl font-black text-gray-900">User Event Details</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Points breakdown {userId ? `for User #${userId}` : "for all users"}
                </p>
              </div>
            </div>

            {/* Dropdown Filters and Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto justify-end">
              {userId && (
                <Link
                  to={`/user/${userId}`}
                  className="px-4 py-2.5 bg-black text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition shadow-2xs flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  title="Open User Detail Page"
                >
                  <FiUser size={16} />
                  View User
                </Link>
              )}
              {/* Event Filter */}
              {eventsList.length > 0 && (
                <div className="w-full sm:w-auto min-w-[180px]">
                  <select
                    value={selectedEventId}
                    onChange={(e) => {
                      setSelectedEventId(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="bg-white border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-black focus:border-black block w-full p-2.5 shadow-2xs outline-none cursor-pointer"
                  >
                    <option value="all">All Events</option>
                    {eventsList.map((ev) => {
                      const evId = ev.eventId || ev.id || ev._id;
                      return (
                        <option key={evId} value={evId}>
                          {ev.eventName || evId}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Source Filter */}
              <div className="w-full sm:w-auto min-w-[160px]">
                <select
                  value={selectedSource}
                  onChange={(e) => {
                    setSelectedSource(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-white border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-black focus:border-black block w-full p-2.5 shadow-2xs outline-none cursor-pointer"
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Clean Simple Table */}
          {loading ? (
            <div className="flex items-center justify-center p-12 bg-white rounded-2xl border border-gray-100">
              <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-xs text-gray-500 uppercase font-bold tracking-wider">
                      <th className="px-6 py-4">Source</th>
                      <th 
                        className="px-6 py-4 cursor-pointer hover:bg-gray-200 transition select-none"
                        onClick={() => handleSort('signedAmt')}
                        title="Click to sort"
                      >
                        Points {sortConfig.key === 'signedAmt' ? (sortConfig.direction === 'ascending' ? '↑' : '↓') : ''}
                      </th>
                      <th className="px-6 py-4">Points After</th>
                      <th className="px-6 py-4">Start Time</th>
                      <th className="px-6 py-4">End Time</th>
                      <th className="px-6 py-4">Duration</th>
                      <th className="px-6 py-4">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sortedHistory.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                          No event points history found for this criteria.
                        </td>
                      </tr>
                    ) : (
                      sortedHistory.map((tx) => {
                        const isPositive = tx.signedAmt > 0;
                        return (
                          <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 font-semibold text-gray-800">
                              {tx.sourceName}
                            </td>
                            <td className="px-6 py-4 font-mono font-bold">
                              <span className={tx.isDebit ? "text-red-600" : "text-green-600"}>
                                {isPositive ? `+${formatPoints(tx.amount)}` : `-${formatPoints(tx.amount)}`}
                              </span>
                            </td>
                            <td className="px-6 py-4 font-mono text-gray-700 font-semibold">
                              {tx.pointsAfter != null
                                ? formatPoints(tx.pointsAfter)
                                : tx.balanceAfter != null
                                ? formatPoints(tx.balanceAfter)
                                : "—"}
                            </td>
                            <td className="px-6 py-4 text-gray-500 text-xs">
                              {tx.startTime ? formatDateTime(tx.startTime) : "—"}
                            </td>
                            <td className="px-6 py-4 text-gray-500 text-xs">
                              {tx.endTime ? formatDateTime(tx.endTime) : "—"}
                            </td>
                            <td className="px-6 py-4 text-gray-500 text-xs font-semibold">
                              {calculateDuration(tx.startTime, tx.endTime)}
                            </td>
                            <td className="px-6 py-4 text-gray-500 text-xs">
                              {formatDateTime(tx.createdAt)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Simple Pagination Controls */}
              {totalRecords > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <span className="text-sm text-gray-500">
                    Showing {(currentPage - 1) * limit + 1} to{" "}
                    {Math.min(currentPage * limit, totalRecords)} of {totalRecords} entries
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1 || loading}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-100 transition cursor-pointer"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1.5 text-sm font-medium text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      disabled={currentPage >= totalPages || loading}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-100 transition cursor-pointer"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
